import { Injectable } from '@angular/core';
import { ConnectionHandler, SessionHandler } from '@sendbird/chat';
import { v4 } from 'uuid';
import { AccountService } from 'src/app/shared/services/account.service';
import { SendBirdUser } from './sb-connection-constants';
import { SbConnectionGqlService } from './sb-connection-gql.service';
import { SbInstanceService } from './sb-instance.service';

/**
 * This service will maintain sendbird's connection and session handlers.
 */
@Injectable({
  providedIn: 'root',
})
export class SbConnectionHandlersService {
  // Unique connection handler key
  private connectionHandlerKey: string;

  constructor(
    private sbInstanceService: SbInstanceService,
    private sbConnectionGqlService: SbConnectionGqlService,
    private accountService: AccountService,
  ) {
    // Generating random id with uuid.
    this.connectionHandlerKey = v4();

    this.createConnectionHandler(this.connectionHandlerKey);
  }

  /**
   * Detects changes in the connection status of a client app. If you want to keep informed of changes related to the Sendbird server connection, define and register multiple connection event handlers. In our case we are only using one handler.
   * Https://sendbird.com/docs/chat/v3/javascript/guides/event-handler#2-add-and-remove-a-connection-event-handler.
   *
   * @param key - Handler unique id.
   */
  createConnectionHandler(key: string): void {
    // TODO: GE-368 - Cleanup after chat refactor completion
    // Remove console message after QA.
    const handler = new ConnectionHandler({
      onConnected: () => {
        console.log('onConnected');
      },
      onDisconnected: () => {
        console.log('onDisconnected');
      },
      onReconnectStarted: () => {
        console.log('onReconnectStarted');
      },
      onReconnectSucceeded: () => {
        console.log('onReconnectSucceeded');
      },
      onReconnectFailed: () => {
        console.log('onReconnectFailed');
        // Disabling it for now as for a proper fix a connection observable will be added.
        // this.utilityService.showErrorAlert(`Something went wrong. Please relaunch the app. If the problem persists, please contact ${settings.brand.name} for support.`);
        // this.authService.userAuthStatusSubject
        //   .pipe(
        //     take(1),
        //   )
        //   .subscribe((isLoggedIn) => {
        //     // GE-456: Only show error when the user is logged in.
        //     if (isLoggedIn) {
        //       this.utilityService.showErrorAlert('Something went wrong. Please refresh the page. If the problem persists, please contact Mable for support.');
        //     }
        //   });
      },
    });

    this.sbInstanceService.instance.addConnectionHandler(key, handler);
  }

  /**
   * When the user session token expires, it will be automatically refreshed using SessionHandler.
   */
  setSessionHandler() {
    const sessionHandler = new SessionHandler();
    sessionHandler.onSessionTokenRequired = (onSuccess, onError) => {
      // TODO: GE-368 - Cleanup after chat refactor completion
      console.log('token required');
      this.refreshSendbirdSessionToken().then(
        (token) => onSuccess(token),
        (e) => {
          console.error('sendbird token refresh failed', e);
          onError(e);
        },
      );
      // A new session token is required in the SDK to refresh the session.
      // Refresh the session token and pass it onto the SDK through onSuccess(NEW_TOKEN).
      // If you do not want to refresh the session, pass on a null value through onSuccess(null).
      // If any error occurred while refreshing the token, let the SDK know about it through onError().
    };
    sessionHandler.onSessionClosed = () => {
      // TODO: GE-368 - Cleanup after chat refactor completion
      console.log('onSessionClosed');

      // The session refresh has been denied from the app.
      // Client app should guide the user to a login page to log in again.
    };
    sessionHandler.onSessionRefreshed = () => {
      // TODO: GE-368 - Cleanup after chat refactor completion
      console.log('onSessionRefreshed');

      // OPTIONAL. No action is required.
      // Called when the session is refreshed.
    };
    // sessionHandler.onSessionError = (err: SendBirdError) => {
    sessionHandler.onSessionError = (error) => {
      // TODO: GE-368 - Cleanup after chat refactor completion
      console.error('onSessionError', error);
      // OPTIONAL. No action is required.
      // Called when an error occurred during the session refresh.
    };

    this.sbInstanceService.instance.setSessionHandler(sessionHandler);
  }

  /**
   * Returns session token for the logged in user if its a directed permitted account, else the selected client's session token if its a coordinator.
   *
   * @returns - Session token generated by backend.
   */
  async refreshSendbirdSessionToken(): Promise<string> {
    try {
      // Current logged in user
      const currentUser: SendBirdUser = this.sbInstanceService.currentUser;

      const isLoggedInUserCoordinator = this.accountService.userIs('coordinator');

      // Pass clientUID only if its a directPermittedAccount.
      // Not checking account type of client because refresh token will only be called after sendbird is connected once.
      // So for coordinator it will be only called if client account type is connected.
      // Only coordinators are allowed to fetch client's token on behalf of their clients.
      return this.sbConnectionGqlService.generateSendbirdSessionToken(
        isLoggedInUserCoordinator ? `${currentUser?.userId}` : undefined,
      );
    } catch (e) {
      console.error('refreshSendbirdSessionToken', e);
      Promise.reject(e);
    }
  }

  /**
   * Removes connection handler.
   */
  remove(): void {
    this.sbInstanceService.instance.removeConnectionHandler(this.connectionHandlerKey);
  }

  /**
   * Calls initiate auto-reconnect manually.
   *
   * TODO: Revisit in refactoring - phase 2.
   */
  reconnect(): void {
    this.sbInstanceService.instance.reconnect();
  }
}
